/**
 * Buzz me when they are close.
 *
 * Nobody wants to stare at an arrow for eleven minutes. The honest shape of most sessions is:
 * check the direction, put the phone in a pocket, walk, and look again when it matters. Right
 * now the product has no way to say "when it matters" — so people either watch the screen the
 * whole way or miss the moment entirely.
 *
 * One threshold, one buzz, one notification. Deliberately not a stream of updates: an alert that
 * fires repeatedly is an alert people turn off, and then the one that mattered never arrives.
 */

/** Offered ranges. Two hundred metres is "start looking up"; fifty is "they are right there". */
export const ALERT_RANGES = [50, 100, 200] as const;

export type AlertRange = (typeof ALERT_RANGES)[number];

/** Hysteresis, so somebody hovering on the threshold does not re-arm and re-fire repeatedly. */
const REARM_MARGIN_M = 25;

export interface AlertNotifier {
  vibrate: (pattern: number[]) => void;
  notify: (title: string, body: string) => void;
}

/**
 * The default notifier: a system notification where permission was granted, a vibration where
 * the hardware supports it, and silence where neither is available.
 *
 * Both are best-effort by design. This feature is a convenience layered on top of a screen that
 * already works; it must never be the reason something throws.
 */
export const systemNotifier: AlertNotifier = {
  vibrate: (pattern) => {
    try {
      navigator.vibrate?.(pattern);
    } catch {
      // Vibration is a nicety. A phone that refuses is not a problem worth surfacing.
    }
  },
  notify: (title, body) => {
    try {
      if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
      // `renotify`-free and tagged, so a second alert replaces the first in the shade rather
      // than stacking up a column of them.
      new Notification(title, { body, tag: 'beeline-arrival', icon: '/icons/icon-192.png' });
    } catch {
      // Some browsers throw here rather than returning a denied permission. Same outcome.
    }
  },
};

/** Ask for notification permission. Must be called from a tap, like every other permission. */
export async function requestAlertPermission(): Promise<boolean> {
  if (typeof Notification === 'undefined') return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;

  try {
    return (await Notification.requestPermission()) === 'granted';
  } catch {
    return false;
  }
}

export const alertsAreSupported = (): boolean =>
  typeof Notification !== 'undefined' || typeof navigator?.vibrate === 'function';

/**
 * Watches the distance and fires once, on the way in.
 *
 * "On the way in" is the whole subtlety. A session that *starts* inside the range must not fire
 * immediately — that would buzz the instant you open the link while standing next to somebody —
 * and a distance that wobbles across the threshold must not fire twice.
 */
export class ArrivalAlert {
  private range: AlertRange | null = null;
  private armed = false;
  private fired = false;

  constructor(private readonly notifier: AlertNotifier = systemNotifier) {}

  /** Turn it on at a range, or off with null. Turning it on again resets the one-shot. */
  set(range: AlertRange | null): void {
    this.range = range;
    this.armed = false;
    this.fired = false;
  }

  get enabledAt(): AlertRange | null {
    return this.range;
  }

  get hasFired(): boolean {
    return this.fired;
  }

  /**
   * Feed the current distance.
   *
   * @returns true on the single update where the alert fires.
   */
  update(distanceMetres: number | null, name: string | null): boolean {
    const range = this.range;
    if (range === null || distanceMetres === null || !Number.isFinite(distanceMetres)) return false;

    // Arm only once we are demonstrably outside the range. Opening the app while already
    // inside it means there is nothing to be alerted about.
    if (!this.armed) {
      if (distanceMetres > range + REARM_MARGIN_M) this.armed = true;
      return false;
    }

    if (this.fired || distanceMetres > range) return false;

    this.fired = true;
    this.armed = false;

    const who = name ?? 'They';
    this.notifier.vibrate([220, 90, 220, 90, 340]);
    this.notifier.notify(`${who} ${name ? 'is' : 'are'} close`, `Under ${range} m away — look up.`);
    return true;
  }

  /** Start again, e.g. after the arrow switches to somebody else. */
  reset(): void {
    this.armed = false;
    this.fired = false;
  }
}
